<template>
  <div class="timeline-tree-container">
    <div
      v-if="id != null"
    >
      <v-header
        :song="song"
        :artist="artist"
      />
      <div class="svg-container">
        <svg 
          v-if="tree != null"
          :width="tree.width + tree.margin.left + tree.margin.right"
          :height="tree.height + tree.margin.top + tree.margin.bottom"
        >
          <g :transform="`translate(${tree.margin.left},${tree.margin.top})`">
            
            <g class="links">
              <g
                v-for="(link, i) in tree.links"
                :key="i"
                class="link"
              >
                <path
                  :d="tree.diagonal({source: link.source, target: link.target})"
                  :stroke-width="pathStrokeWidth(link)"
                  :stroke="pathStroke(link)"
                  fill="transparent"
                  opacity="1"
                />
              </g>
            </g>

            <g class="nodes">
              <g
                v-for="(node, i) in tree.nodes"
                :key="i"
              >

                <g class="node">
                  <circle
                    v-show="node.data.id == id"
                    :cx="node.x"
                    :cy="node.y"
                    fill="transparent"
                    stroke-width="1"
                    stroke="#a1a1a1"
                    r="9"
                  />
                  <circle
                    class="dot"
                    r="4.5"
                    :cx="node.x"
                    :cy="node.y"
                    :fill="dotColor(node.data)"
                    :stroke="dotStroke(node.data)"
                    :stroke-width="dotStrokeWidth(node.data)"
                  />
                  <circle
                    :cx="node.x"
                    :cy="node.y"
                    fill="transparent"
                    r="16"
                    @click="onSongClick(node.data.id)"
                    @mouseenter="onMouseover(node.data.id)"
                    @mouseleave="onMouseover(null)"
                  />
                </g>
              </g>
            </g>

          </g>        
        </svg>
      </div>
      <div
        v-show="hasWhiteCircle && !hideTuto"
        class="timeline-tree-footer"
      >
        <div class="timeline-tree-footer-container">
          White circles are songs <b>having no direct adaptation relationship</b> with the artist.
        </div>
        <div
          class="timeline-tree-footer-close"
          @click="hideTuto = true"
        >
          <div class="mdi mdi-close" />
        </div>
      </div>
    </div>
    <div
      v-else
      class="empty-container"
    >
      No song selected
    </div>
  </div>
</template>

<script>

import { isNumber } from "lodash";
import { Tree } from "./tree.js";
import { rgb } from "d3";
import Header from "./Header";

export default {
	components: {
		"v-header": Header
	},
	props: {
		id: {
			required: false,
			default: null
		},
		mouseover: {
			required: false,
			default: null
		},
		artistMouseover: {
			required: true
		},
		artist: {
			required: true,
			type: Object
		}
	},
	data () {
		return {
			tree: null,
			hideTuto: false
		};
	},
	computed: {
		song () {
			if (this.id != null) {
				return this.$store.state.songs[this.id];
			}
			return null;
		},
		hasWhiteCircle () {
			if (this.tree == null) {
				return false;
			}
			for (let i = 0; i < this.tree.nodes.length; i++) {
				if (this.dotColor(this.tree.nodes[i].data) == this.$parent.timeline.colors.na) {
					return true;
				}
			}
			return false;
		}
	},
	watch: {
		id () {
			this.render();
		}
	},
	created () {
		this.render();
	},
	methods: {
		render () {
			if (this.id != null) {
				this.tree = new Tree(this.id, this.$parent.timeline);
			}
		},
		dotActive (song) {
			return song.id == this.mouseover;
		},
		dotColor (song) {
			return this.$parent.timeline.color(song.level);
		},
		dotStroke (song) {
			return rgb(this.dotColor(song)).darker(this.dotActive(song) ? 0.7 : 1);
		},
		dotStrokeWidth (song) {
			return this.dotActive(song) ? 2 : 1;
		},
		pathStroke (link) {
			if (link.source.data.artist_ids.includes(this.artist.id))  {
				return this.$parent.timeline.colors.target;
			}
			if (link.target.data.artist_ids.includes(this.artist.id)) {
				return this.$parent.timeline.colors.source;
			}
			return "#e1e1e1";
		},
		pathStrokeWidth (link) {
			if (this.mouseover == link.source.data.id) {
				return 2;
			}
			if (this.mouseover == link.target.data.id) {
				return 2;
			}
			if (link.target.data.artist_ids.includes(this.artistMouseover.id) && this.artistMouseover.level == "target") {
				return 2;
			}

			if (link.source.data.artist_ids.includes(this.artistMouseover.id) && this.artistMouseover.level == "source") {
				return 2;
			}

			return 1;
		},
		onMouseover (id) {
			this.$emit("onMouseover", id);
		},
		onSongClick (id) {
			this.$emit("onClick", id);
		},
	}
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.timeline-tree-container {
  border: 1px #f1f1f1 solid;
  height: 316px;
  position: relative;
}

.svg-container {
  position: relative;
  display: block;
  overflow-y: auto;
  overflow-x: auto;
  text-align: center;
  width: 100%;
  height: 245px;

  svg {
    margin: 0 auto;
  }
}

.timeline-tree-footer {
  position: absolute;
  bottom: 0;
  width: 100%;
  font-size: 0.9rem;
  padding: 0.6rem;
  line-height: 1.12;

  .timeline-tree-footer-container {
    border-radius: 3px;
    background-color: rgba(255,255,255,0.8);
    padding: 0.5rem 0.75rem;
    padding-right: 3rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.25);
  }

  .timeline-tree-footer-close {
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;
  }
}

.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

</style>

